import { useCallback, useEffect, useRef, useState } from "react";
import { ArticleNonParagraphProps } from "@pages/Article/components/ArticleParagraphElements/utils/types";
import { cn } from "@util/helpers";
import parse, { DOMNode, domToReact, Element } from "html-react-parser";

/**
 * Renders a DataWrapper element that contains an iframe for displaying data visualizations.
 * It listens for messages from the iframe to adjust its height dynamically.
 */
const DataWrapper = ({ bodyString, className }: ArticleNonParagraphProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(500);

  const onMessage = useCallback(
    ({ data = {}, source }: MessageEvent) => {
      if (
        source !== iframeRef.current?.contentWindow ||
        typeof data === "string" ||
        !data["datawrapper-height"]
      ) {
        return;
      }

      const actualHeight = Object.values(data["datawrapper-height"])?.[0];

      if (actualHeight && typeof actualHeight === "number") {
        setHeight(actualHeight);
      }
    },
    [iframeRef]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  const transformDatawrapper = (
    node: DOMNode
  ): string | boolean | void | object | Element | null => {
    if (node.type === "tag" && node.name === "iframe") {
      const el = node;

      const children = el.children as DOMNode[];

      return (
        <iframe
          ref={iframeRef}
          className={cn("mb-6", className)}
          data-height={height}
          style={{
            width: "100%",
            height,
            border: 0,
            boxSizing: "content-box",
          }}
          src={el.attribs["src"]}
          title="data-wrapper-title"
          scrolling="no"
        >
          {domToReact(children)}
        </iframe>
      );
    }
  };

  return (
    <div data-testid="article-datawrapper-component">
      {parse(bodyString, {
        replace: transformDatawrapper,
      })}
    </div>
  );
};

export default DataWrapper;
